/**
 * Writing one activity onto a record — `docs/quiet-records.md` §4.
 *
 * Four lines, lifted out of `RecordActivityTab.post` with nothing changed,
 * because the quiet Overview carries **the real `ActivityComposer`** (§1 rule
 * 7) rather than a lookalike: the first note somebody writes on an empty
 * record is what flips the page to the busy Overview, so it has to be the
 * same write, hit the same endpoint, and reload the same summary.
 *
 * A second copy would have been four lines too, which is exactly why it would
 * have been written — and then one of them would have forgotten `onChanged`,
 * and the note would have landed with the tab pill still reading zero.
 */
import { useCallback, useState } from 'react'
import type { RecordType } from '@rowted/shared'
import { request } from '../lib/api.js'

/** What `ActivityComposer` submits. Its own shape, not re-stated here. */
export type ActivityDraft = Record<string, unknown>

export interface ActivityPost {
  post: (draft: ActivityDraft) => Promise<void>
  busy: boolean
  /**
   * Bumped after every write.
   *
   * `ActivityTimeline` takes it as `reloadKey`; the quiet panel ignores it,
   * because the panel is gone by the time there is a timeline to reload.
   */
  reloadKey: number
  /**
   * Something reached the record another way — a text or an email sent from
   * its *Email* and *Text* (`record-message.tsx`): the timeline and the
   * counts reload as after a write.
   */
  refresh: () => void
}

export function useActivityPost(
  object: RecordType,
  id: string,
  /** The summary owns the counts, so the page reloads it after a write. */
  onChanged: () => void,
): ActivityPost {
  const [busy, setBusy] = useState(false)
  const [reloadKey, setReloadKey] = useState(0)

  const post = useCallback(async (draft: ActivityDraft) => {
    setBusy(true)
    try {
      await request(`/records/${object}/${id}/activities`, { method: 'POST', body: draft })
      setReloadKey((key) => key + 1)
      onChanged()
    } finally {
      setBusy(false)
    }
  }, [object, id, onChanged])

  const refresh = useCallback(() => {
    setReloadKey((key) => key + 1)
    onChanged()
  }, [onChanged])

  return { post, busy, reloadKey, refresh }
}
